import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronRight,
  ArrowLeft,
  Home,
  MapPin,
  Building,
  Layers,
  Map,
  Folder,
  FileText,
  Download,
  Filter,
  Search,
  TrendingUp,
  DollarSign,
  BarChart3,
  RefreshCw,
  ArrowUpDown,
} from 'lucide-react';

// Types
interface BaseEntity {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
}

interface WorksSummary {
  total_works: number;
  total_estimate_cost: number;
  total_ras_amount: number;
  total_expenditure: number;
  total_length_sanctioned: number;
  total_length_completed: number;
  total_bills_pending: number;
  total_work_not_recorded: number;
  financial_progress_percentage: number;
  physical_progress_percentage: number;
  avg_estimate_cost: number;
  stage_wise_breakdown: {
    [key: string]: { count: number; total_cost: number; total_expenditure: number };
  };
  grant_wise_breakdown: {
    [key: string]: { count: number; total_cost: number; total_expenditure: number };
  };
  completed_works?: number;
  in_progress_works?: number;
  delayed_works?: number;
  performance_score?: number;
  budget_utilization_percentage?: number;
}

interface CETerritory extends BaseEntity {
  works_summary?: WorksSummary;
}

interface Circle extends BaseEntity {
  ce_territory_id: number;
  works_summary?: WorksSummary;
}

interface District extends BaseEntity {
  circle_id: number;
  works_summary?: WorksSummary;
}

interface Division extends BaseEntity {
  district_id: number;
  works_summary?: WorksSummary;
}

interface SubDivision extends BaseEntity {
  division_id: number;
  works_summary?: WorksSummary;
}

interface Mandal extends BaseEntity {
  sub_division_id: number;
  mandal_type: 'URBAN' | 'RURAL';
  works_summary?: WorksSummary;
}

interface Work {
  id: number;
  sno: number;
  work_name: string;
  estimate_cost: number;
  expenditure: number;
  abstract_stage_of_work: string;
  grant_type: string;
  agency_name: string;
  oas_sanction_date: string;
  financial_progress_percentage: number;
  physical_progress_percentage: number;
  length_sanctioned_km?: number;
  length_completed?: number;
  contractor?: {
    contractor_name: string;
    firm_name: string;
  };
}

interface BreadcrumbItem {
  id: number;
  name: string;
  type: 'territory' | 'circle' | 'district' | 'division' | 'subdivision' | 'mandal';
}

interface HierarchicalWorkResponse {
  summary: WorksSummary;
  entity_details: {
    id: number;
    code: string;
    name: string;
    type: string;
  };
  children?: any[];
  works?: Work[];
}

interface HierarchicalFilters {
  grantLevel?: string;
  grantType?: string;
  stage?: string;
  searchTerm?: string;
  minCost?: number;
  maxCost?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

// API Service Class
class HierarchicalWorksAPI {
  private baseURL: string;

  constructor(baseURL: string = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1') {
    this.baseURL = baseURL;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          ...options?.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw error;
    }
  }

  async getCETerritories(filters?: HierarchicalFilters): Promise<CETerritory[]> {
    const params = this.buildQueryParams({ ...filters, includeStats: true });
    const response = await this.request<HierarchicalWorkResponse[]>(`/works/hierarchical/ce-territories${params}`);
    return response.map((item) => ({
      id: item.entity_details.id,
      name: item.entity_details.name,
      code: item.entity_details.code,
      is_active: true,
      works_summary: item.summary,
    }));
  }

  async getCETerritoryDetails(territoryId: number, filters?: HierarchicalFilters): Promise<HierarchicalWorkResponse> {
    const params = this.buildQueryParams({ ...filters, includeChildren: true, includeStats: true });
    return this.request(`/works/hierarchical/ce-territory/${territoryId}${params}`);
  }

  async getCirclesByTerritory(territoryId: number, filters?: HierarchicalFilters): Promise<Circle[]> {
    const params = this.buildQueryParams({ ...filters, includeStats: true, includeChildren: true });
    const response = await this.request<HierarchicalWorkResponse>(`/works/hierarchical/ce-territory/${territoryId}${params}`);
    return (response.children || []).map((child: any) => ({
      id: child.id, // Directly use id from child, as per JSON structure
      name: child.name,
      code: child.code,
      ce_territory_id: territoryId,
      is_active: true,
      works_summary: child.works_summary,
    }));
  }

  async getDistrictsByCircle(circleId: number, filters?: HierarchicalFilters): Promise<District[]> {
    const params = this.buildQueryParams({ ...filters, includeStats: true, includeChildren: true });
    const response = await this.request<HierarchicalWorkResponse>(`/works/hierarchical/circle/${circleId}${params}`);
    return (response.children || []).map((child: any) => ({
      id: child.id,
      name: child.name,
      code: child.code,
      circle_id: circleId,
      is_active: true,
      works_summary: child.works_summary,
    }));
  }

  async getDivisionsByDistrict(districtId: number, filters?: HierarchicalFilters): Promise<Division[]> {
    const params = this.buildQueryParams({ ...filters, includeStats: true, includeChildren: true });
    const response = await this.request<HierarchicalWorkResponse>(`/works/hierarchical/district/${districtId}${params}`);
    return (response.children || []).map((child: any) => ({
      id: child.id,
      name: child.name,
      code: child.code,
      district_id: districtId,
      is_active: true,
      works_summary: child.works_summary,
    }));
  }

  async getSubDivisionsByDivision(divisionId: number, filters?: HierarchicalFilters): Promise<SubDivision[]> {
    const params = this.buildQueryParams({ ...filters, includeStats: true, includeChildren: true });
    const response = await this.request<HierarchicalWorkResponse>(`/works/hierarchical/division/${divisionId}${params}`);
    return (response.children || []).map((child: any) => ({
      id: child.id,
      name: child.name,
      code: child.code,
      division_id: divisionId,
      is_active: true,
      works_summary: child.works_summary,
    }));
  }

  async getMandalsBySubDivision(subDivisionId: number, filters?: HierarchicalFilters): Promise<Mandal[]> {
    const params = this.buildQueryParams({ ...filters, includeStats: true, includeChildren: true });
    const response = await this.request<HierarchicalWorkResponse>(`/works/hierarchical/subdivision/${subDivisionId}${params}`);
    return (response.children || []).map((child: any) => ({
      id: child.id,
      name: child.name,
      code: child.code,
      sub_division_id: subDivisionId,
      mandal_type: child.mandal_type || 'RURAL',
      is_active: true,
      works_summary: child.works_summary,
    }));
  }

  async getWorksByMandal(mandalId: number, filters?: HierarchicalFilters): Promise<Work[]> {
    const params = this.buildQueryParams({ ...filters, includeWorks: true });
    const response = await this.request<HierarchicalWorkResponse>(`/works/hierarchical/mandal/${mandalId}${params}`);
    return response.works || [];
  }

  async exportData(level: string, entityId: number, format: 'json' | 'excel' = 'excel'): Promise<Blob> {
    const params = this.buildQueryParams({ format, includeStats: true });
    const response = await fetch(
      `${this.baseURL}/works/hierarchical/export/${level}/${entityId}${params}`,
      {
        method: 'GET',
        headers: {
          'Accept': format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }

  private buildQueryParams(filters?: HierarchicalFilters & Record<string, any>): string {
    if (!filters) return '';
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    return params.toString() ? `?${params.toString()}` : '';
  }
}

// Create API instance
const hierarchicalAPI = new HierarchicalWorksAPI();

// Utility functions
const formatCurrency = (amount: number): string => {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  return `₹${amount.toLocaleString('en-IN')}`;
};

const formatNumber = (num: number): string => num.toLocaleString('en-IN');
const formatPercentage = (percent: number): string => `${percent.toFixed(1)}%`;

// Hook for managing hierarchy state
const useHierarchicalData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLevel, setCurrentLevel] = useState<string>('territory');
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [works, setWorks] = useState<Work[]>([]);
  const [summary, setSummary] = useState<WorksSummary | null>(null);
  const [filters, setFilters] = useState<HierarchicalFilters>({
    sortBy: 'name',
    sortOrder: 'ASC',
    page: 1,
    limit: 50,
  });

  const fetchData = useCallback(
    async (level: string, entityId?: number) => {
      setLoading(true);
      setError(null);

      try {
        let response: any;

        switch (level) {
          case 'territory':
            response = await hierarchicalAPI.getCETerritories(filters);
            setEntities(response);
            setSummary(null);
            setWorks([]);
            break;

          case 'circle':
            if (!entityId) throw new Error('Territory ID required for circles');
            response = await hierarchicalAPI.getCirclesByTerritory(entityId, filters);
            setEntities(response);
            const territoryResponse = await hierarchicalAPI.getCETerritoryDetails(entityId, filters);
            setSummary(territoryResponse.summary);
            setWorks([]);
            break;

          case 'district':
            if (!entityId) throw new Error('Circle ID required for districts');
            response = await hierarchicalAPI.getDistrictsByCircle(entityId, filters);
            setEntities(response);
            setSummary(null);
            setWorks([]);
            break;

          case 'division':
            if (!entityId) throw new Error('District ID required for divisions');
            response = await hierarchicalAPI.getDivisionsByDistrict(entityId, filters);
            setEntities(response);
            setSummary(null);
            setWorks([]);
            break;

          case 'subdivision':
            if (!entityId) throw new Error('Division ID required for sub-divisions');
            response = await hierarchicalAPI.getSubDivisionsByDivision(entityId, filters);
            setEntities(response);
            setSummary(null);
            setWorks([]);
            break;

          case 'mandal':
            if (!entityId) throw new Error('Sub-division ID required for mandals');
            response = await hierarchicalAPI.getMandalsBySubDivision(entityId, filters);
            setEntities(response);
            setSummary(null);
            setWorks([]);
            break;

          case 'works':
            if (!entityId) throw new Error('Mandal ID required for works');
            response = await hierarchicalAPI.getWorksByMandal(entityId, filters);
            setWorks(response);
            setEntities([]);
            setSummary(null);
            break;

          default:
            throw new Error(`Unknown level: ${level}`);
        }

        setCurrentLevel(level);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  const navigateTo = useCallback(
    (level: string, entity?: any, entityId?: number) => {
      const id = entityId || entity?.id;

      if (entity) {
        const newBreadcrumbItem: BreadcrumbItem = {
          id: entity.id,
          name: entity.name,
          type: level as any,
        };
        setBreadcrumb((prev) => [...prev, newBreadcrumbItem]);
      }

      fetchData(level, id);
    },
    [fetchData]
  );

  const goBack = useCallback(() => {
    if (breadcrumb.length === 0) return;

    const newBreadcrumb = [...breadcrumb];
    newBreadcrumb.pop();
    setBreadcrumb(newBreadcrumb);

    if (newBreadcrumb.length === 0) {
      fetchData('territory');
    } else {
      const parent = newBreadcrumb[newBreadcrumb.length - 1];
      const levelMap: Record<string, string> = {
        circle: 'territory',
        district: 'circle',
        division: 'district',
        subdivision: 'division',
        mandal: 'subdivision',
        works: 'mandal',
      };
      fetchData(levelMap[parent.type], parent.id);
    }
  }, [breadcrumb, fetchData]);

  const goHome = useCallback(() => {
    setBreadcrumb([]);
    fetchData('territory');
  }, [fetchData]);

  const updateFilters = useCallback((newFilters: HierarchicalFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const exportData = useCallback(
    async (format: 'json' | 'excel' = 'excel') => {
      if (!breadcrumb.length) return;

      try {
        const lastItem = breadcrumb[breadcrumb.length - 1];
        const blob = await hierarchicalAPI.exportData(lastItem.type, lastItem.id, format);

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${lastItem.name}_${lastItem.type}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'json'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Export failed');
      }
    },
    [breadcrumb, setError]
  );

  return {
    loading,
    error,
    setError,
    currentLevel,
    breadcrumb,
    entities,
    works,
    summary,
    filters,
    navigateTo,
    goBack,
    goHome,
    updateFilters,
    exportData,
    clearError: () => setError(null),
    fetchData,
    setBreadcrumb,
  };
};

// Components
const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center p-12">
    <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

const ErrorDisplay: React.FC<{ error: string; onDismiss: () => void }> = ({ error, onDismiss }) => (
  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4" role="alert">
    <div className="flex justify-between items-center">
      <span>{error}</span>
      <button onClick={onDismiss} className="text-red-500 hover:text-red-700" aria-label="Dismiss error">
        ×
      </button>
    </div>
  </div>
);

const Breadcrumb: React.FC<{ items: BreadcrumbItem[]; onNavigate: (index: number) => void; onHome: () => void }> = ({
  items,
  onNavigate,
  onHome,
}) => (
  <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
    <button onClick={onHome} className="flex items-center hover:text-blue-600 transition-colors">
      <Home className="w-4 h-4" />
      <span className="ml-1">Home</span>
    </button>
    {items.map((item, index) => (
      <React.Fragment key={`${item.id}-${item.type}`}>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <button
          onClick={() => onNavigate(index)}
          className="hover:text-blue-600 transition-colors capitalize"
        >
          {item.name}
        </button>
      </React.Fragment>
    ))}
  </nav>
);

const SummaryCards: React.FC<{ summary: WorksSummary }> = ({ summary }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">Total Works</p>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(summary.total_works)}</p>
        </div>
        <FileText className="w-8 h-8 text-blue-500" />
      </div>
    </div>

    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">Total Investment</p>
          <p className="text-lg font-bold text-gray-900">{formatCurrency(summary.total_estimate_cost)}</p>
        </div>
        <DollarSign className="w-8 h-8 text-green-500" />
      </div>
    </div>

    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">Financial Progress</p>
          <p className="text-lg font-bold text-gray-900">{formatPercentage(summary.financial_progress_percentage)}</p>
        </div>
        <TrendingUp className="w-8 h-8 text-orange-500" />
      </div>
      <div className="mt-2">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-orange-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(summary.financial_progress_percentage, 100)}%` }}
          />
        </div>
      </div>
    </div>

    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">Performance Score</p>
          <p className="text-lg font-bold text-gray-900">{summary.performance_score || 0}/100</p>
        </div>
        <BarChart3 className="w-8 h-8 text-purple-500" />
      </div>
      <div className="mt-2">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-purple-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${summary.performance_score || 0}%` }}
          />
        </div>
      </div>
    </div>
  </div>
);

const FilterPanel: React.FC<{
  filters: HierarchicalFilters;
  onFiltersChange: (filters: HierarchicalFilters) => void;
  currentLevel: string;
  isExpanded: boolean;
  onToggle: () => void;
  activeFiltersCount: number;
}> = ({ filters, onFiltersChange, currentLevel, isExpanded, onToggle, activeFiltersCount }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Filter className="w-5 h-5 mr-2 text-blue-600" />
            Filters
          </h3>
          <div className="flex items-center space-x-4">
            {activeFiltersCount > 0 && (
              <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                {activeFiltersCount} active
              </span>
            )}
            <button
              onClick={onToggle}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              {isExpanded ? 'Collapse' : 'Expand Filters'}
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={`Search ${currentLevel}s...`}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={filters.searchTerm || ''}
            onChange={(e) => onFiltersChange({ ...filters, searchTerm: e.target.value })}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grant Level</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors"
                value={filters.grantLevel || ''}
                onChange={(e) => onFiltersChange({ ...filters, grantLevel: e.target.value || undefined })}
              >
                <option value="">All Grant Levels</option>
                <option value="Central">Central</option>
                <option value="State">State</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grant Type</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors"
                value={filters.grantType || ''}
                onChange={(e) => onFiltersChange({ ...filters, grantType: e.target.value || undefined })}
              >
                <option value="">All Grant Types</option>
                <option value="PMGSY">PMGSY</option>
                <option value="MGNREGS">MGNREGS</option>
                <option value="State-MRR">State-MRR</option>
                <option value="State-CRR">State-CRR</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Work Stage</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors"
                value={filters.stage || ''}
                onChange={(e) => onFiltersChange({ ...filters, stage: e.target.value || undefined })}
              >
                <option value="">All Stages</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Not Started">Not Started</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EntitiesTable: React.FC<{
  entities: any[];
  loading: boolean;
  currentLevel: string;
  onEntityClick: (entity: any) => void;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  onSort: (field: string) => void;
}> = ({ entities, loading, currentLevel, onEntityClick, sortBy, sortOrder, onSort }) => {
  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 opacity-40" />;
    return sortOrder === 'ASC' ? (
      <ArrowUpDown className="w-4 h-4 text-blue-600" />
    ) : (
      <ArrowUpDown className="w-4 h-4 text-blue-600 rotate-180" />
    );
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'territory':
        return <Map className="w-5 h-5 text-blue-600" />;
      case 'circle':
        return <Layers className="w-5 h-5 text-blue-600" />;
      case 'district':
        return <MapPin className="w-5 h-5 text-blue-600" />;
      case 'division':
        return <Building className="w-5 h-5 text-blue-600" />;
      case 'subdivision':
        return <Folder className="w-5 h-5 text-blue-600" />;
      case 'mandal':
        return <Building className="w-5 h-5 text-blue-600" />;
      default:
        return <MapPin className="w-5 h-5 text-blue-600" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading {currentLevel}s...</p>
        </div>
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-12 text-center">
          <Map className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No {currentLevel}s Found</h3>
          <p className="text-gray-600">No {currentLevel}s match your current filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>{currentLevel.charAt(0).toUpperCase() + currentLevel.slice(1)}</span>
                  {getSortIcon('name')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('total_works')}
              >
                <div className="flex items-center space-x-1">
                  <span>Works</span>
                  {getSortIcon('total_works')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('total_estimate_cost')}
              >
                <div className="flex items-center space-x-1">
                  <span>Total Value</span>
                  {getSortIcon('total_estimate_cost')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Progress
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status Breakdown
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onSort('performance_score')}
              >
                <div className="flex items-center space-x-1">
                  <span>Performance</span>
                  {getSortIcon('performance_score')}
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {entities.map((entity) => {
              const completedWorks = entity.works_summary?.stage_wise_breakdown?.['Completed']?.count || 0;
              const inProgressWorks = entity.works_summary?.stage_wise_breakdown?.['In Progress']?.count || 0;
              const delayedWorks = entity.works_summary?.stage_wise_breakdown?.['On Hold']?.count || 0;
              const performanceScore = entity.works_summary?.performance_score || 0;

              return (
                <tr
                  key={entity.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer group"
                  onClick={() => onEntityClick(entity)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          {getLevelIcon(currentLevel)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{entity.name}</div>
                        <div className="text-sm text-gray-500">{entity.code}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-semibold text-gray-900">{formatNumber(entity.works_summary?.total_works || 0)}</div>
                      <div className="text-gray-500 text-xs">
                        <span className="text-green-600">{completedWorks} completed</span>
                        {delayedWorks > 0 && (
                          <span className="text-red-600 ml-2">{delayedWorks} delayed</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-semibold text-gray-900">{formatCurrency(entity.works_summary?.total_estimate_cost || 0)}</div>
                      <div className="text-gray-500 text-xs">
                        Spent: {formatCurrency(entity.works_summary?.total_expenditure || 0)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Financial</span>
                        <span className="font-medium">{formatPercentage(entity.works_summary?.financial_progress_percentage || 0)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(entity.works_summary?.financial_progress_percentage || 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {inProgressWorks > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {inProgressWorks} In Progress
                        </span>
                      )}
                      {completedWorks > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {completedWorks} Completed
                        </span>
                      )}
                      {delayedWorks > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {delayedWorks} Delayed
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center">
                        <div
                          className={`w-3 h-3 rounded-full mr-2 ${
                            performanceScore >= 80 ? 'bg-green-500' : performanceScore >= 60 ? 'bg-blue-500' : 'bg-yellow-500'
                          }`}
                        />
                        <span
                          className={`text-sm font-medium ${
                            performanceScore >= 80 ? 'text-green-600' : performanceScore >= 60 ? 'text-blue-600' : 'text-yellow-600'
                          }`}
                        >
                          {performanceScore >= 80 ? 'Excellent' : performanceScore >= 60 ? 'Good' : 'Average'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">Score: {performanceScore}/100</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEntityClick(entity);
                      }}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 mr-1" />
                      View Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WorksTable: React.FC<{ works: Work[]; summary?: WorksSummary | null }> = ({ works, summary }) => {
  const [sortBy, setSortBy] = useState<keyof Work>('sno');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const handleSort = (field: keyof Work) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const sortedWorks = [...works].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    const modifier = sortOrder === 'ASC' ? 1 : -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue) * modifier;
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * modifier;
    }
    return 0;
  });

  const getSortIcon = (field: keyof Work) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 opacity-40" />;
    return (
      <ArrowUpDown
        className={`w-4 h-4 text-blue-600 ${sortOrder === 'DESC' ? 'rotate-180' : ''}`}
      />
    );
  };

  return (
    <div className="space-y-4">
      {summary && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Works Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{formatNumber(summary.total_works)}</p>
              <p className="text-sm text-gray-600">Total Works</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-green-600">{formatCurrency(summary.total_estimate_cost)}</p>
              <p className="text-sm text-gray-600">Total Value</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-orange-600">{formatPercentage(summary.financial_progress_percentage)}</p>
              <p className="text-sm text-gray-600">Financial Progress</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-purple-600">{summary.performance_score || 0}/100</p>
              <p className="text-sm text-gray-600">Performance Score</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('sno')}
                >
                  <div className="flex items-center space-x-1">
                    <span>SNO</span>
                    {getSortIcon('sno')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('work_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Work Name</span>
                    {getSortIcon('work_name')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('estimate_cost')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Estimate Cost</span>
                    {getSortIcon('estimate_cost')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('expenditure')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Expenditure</span>
                    {getSortIcon('expenditure')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Progress
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('abstract_stage_of_work')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Stage</span>
                    {getSortIcon('abstract_stage_of_work')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Grant Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Agency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Sanction Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedWorks.map((work) => (
                <tr key={work.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {work.sno}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900 max-w-xs truncate">
                        {work.work_name}
                      </div>
                      {work.contractor && (
                        <div className="text-gray-500 text-xs">
                          {work.contractor.contractor_name}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(work.estimate_cost)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(work.expenditure)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Financial</span>
                        <span className="font-medium">
                          {formatPercentage(work.financial_progress_percentage || 0)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${Math.min(work.financial_progress_percentage || 0, 100)}%` }}
                        />
                      </div>
                      {work.physical_progress_percentage !== undefined && (
                        <>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Physical</span>
                            <span className="font-medium">
                              {formatPercentage(work.physical_progress_percentage)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${Math.min(work.physical_progress_percentage, 100)}%` }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        work.abstract_stage_of_work === 'Completed'
                          ? 'bg-green-100 text-green-800'
                          : work.abstract_stage_of_work === 'In Progress'
                          ? 'bg-blue-100 text-blue-800'
                          : work.abstract_stage_of_work === 'On Hold'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {work.abstract_stage_of_work}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {work.grant_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                    {work.agency_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(work.oas_sanction_date).toLocaleDateString('en-IN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {works.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Works Found</h3>
            <p className="text-gray-600">No works match the current criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
const HierarchicalDrillDown: React.FC = () => {
  const {
    loading,
    error,
    setError,
    currentLevel,
    breadcrumb,
    entities,
    works,
    summary,
    filters,
    navigateTo,
    goBack,
    goHome,
    updateFilters,
    exportData,
    clearError,
    fetchData,
    setBreadcrumb,
  } = useHierarchicalData();

  const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(false);

  // Calculate active filters count
  const activeFiltersCount = React.useMemo(() => {
    return Object.entries(filters).reduce((count, [key, value]) => {
      if (['sortBy', 'sortOrder', 'page', 'limit'].includes(key)) return count;
      return value !== undefined && value !== '' ? count + 1 : count;
    }, 0);
  }, [filters]);

  useEffect(() => {
    fetchData('territory');
  }, [fetchData]);

  const getLevelConfig = (level: string) => {
    const configs = {
      territory: {
        title: 'Territories',
        nextLevel: 'circle',
      },
      circle: {
        title: 'Regions',
        nextLevel: 'district',
      },
      district: {
        title: 'Divisions',
        nextLevel: 'division',
      },
      division: {
        title: 'Sub-Divisions',
        nextLevel: 'subdivision',
      },
      subdivision: {
        title: 'Mandals',
        nextLevel: 'mandal',
      },
      mandal: {
        title: 'Works',
        nextLevel: 'works',
      },
    };
    return configs[level as keyof typeof configs] || { title: level, nextLevel: '' };
  };

  const config = getLevelConfig(currentLevel);

  const handleEntityClick = (entity: any) => {
    if (config) {
      navigateTo(config.nextLevel, entity);
    }
  };

  const handleSort = (field: string) => {
    const newSortOrder = filters.sortBy === field && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    updateFilters({ ...filters, sortBy: field, sortOrder: newSortOrder });
  };

  const handleBreadcrumbNavigate = useCallback(
    (index: number) => {
      // Validate index and breadcrumb item
      if (index < 0 || index >= breadcrumb.length || !breadcrumb[index]) {
        console.error(`Invalid breadcrumb index: ${index}, breadcrumb length: ${breadcrumb.length}`);
        setError('Invalid breadcrumb navigation');
        return;
      }

      const targetItem = breadcrumb[index];
      if (!targetItem || !targetItem.id || !targetItem.type) {
        console.error(`Invalid breadcrumb item at index ${index}:`, targetItem);
        setError('Invalid breadcrumb item');
        return;
      }

      const newBreadcrumb = breadcrumb.slice(0, index + 1);
      setBreadcrumb(newBreadcrumb);

      // Map breadcrumb type to the correct level for fetching data
      const levelMap: Record<string, string> = {
        territory: 'territory',
        circle: 'circle',
        district: 'district',
        division: 'division',
        subdivision: 'subdivision',
        mandal: 'mandal',
        works: 'works',
      };

      const targetLevel = levelMap[targetItem.type];

      if (!targetLevel) {
        console.error(`Unknown breadcrumb type: ${targetItem.type}`);
        setError('Invalid navigation level');
        return;
      }

      // Fetch data for the target level and ID
      fetchData(targetLevel, targetItem.id);
    },
    [breadcrumb, setBreadcrumb, fetchData, setError]
  );

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Territorial Works Management</h1>
            <p className="text-gray-600 mt-1">
              Navigate through the territorial hierarchy to view works and analytics
            </p>
          </div>
          <div className="flex space-x-3">
            {breadcrumb.length > 0 && (
              <button
                onClick={goBack}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
            )}
            {breadcrumb.length > 0 && (
              <button
                onClick={() => exportData('excel')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && <ErrorDisplay error={error} onDismiss={clearError} />}

      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumb} onNavigate={handleBreadcrumbNavigate} onHome={goHome} />

      {/* Summary Cards */}
      {summary && <SummaryCards summary={summary} />}

      {/* Filter Panel */}
      <FilterPanel
        filters={filters}
        onFiltersChange={updateFilters}
        currentLevel={currentLevel}
        isExpanded={isFilterExpanded}
        onToggle={() => setIsFilterExpanded(!isFilterExpanded)}
        activeFiltersCount={activeFiltersCount}
      />

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {currentLevel !== 'works' && config && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{config.title}</h2>
                <p className="text-gray-600">
                  {entities.length} {config.title.toLowerCase()} found
                </p>
              </div>
              <EntitiesTable
                entities={entities}
                loading={loading}
                currentLevel={currentLevel}
                onEntityClick={handleEntityClick}
                sortBy={filters.sortBy || 'name'}
                sortOrder={filters.sortOrder || 'ASC'}
                onSort={handleSort}
              />
            </div>
          )}

          {currentLevel === 'works' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Works Details</h2>
                <p className="text-gray-600">{works.length} works found</p>
              </div>
              <WorksTable works={works} summary={summary} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HierarchicalDrillDown;